import React, { useState } from 'react';
import './styles/FormattedCode.css';
import Rating from './Rating';
import { LabelXSmall, ParagraphSmall } from 'baseui/typography';
import { Block } from 'baseui/block';
import CodeBlock from '../CodeBlock';
import ExtraInfoImage from '../ExtraInfoImage';
import {
  extractMarkdownImages,
  mergeExtraInfoImages,
} from '../../utils/extraInfoImages';

const FENCE_REGEX = /```(\w+)?\s*\n?([\s\S]*?)```/g;
const MD_IMAGE_REGEX = /!\[([^\]]*)\]\(([^)]+)\)/g;

const splitExtraInfoContent = (content) => {
  if (!content?.trim()) return [];

  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = FENCE_REGEX.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        body: content.substring(lastIndex, match.index).trim(),
      });
    }
    const language = (match[1] || 'text').toLowerCase();
    segments.push({ type: 'code', language, body: match[2].trim() });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      body: content.substring(lastIndex).trim(),
    });
  }

  if (segments.length === 0) {
    return [{ type: 'text', body: content.trim() }];
  }

  return segments.filter(
    (seg) => seg.type !== 'text' || (seg.body && seg.body.length > 0)
  );
};

const looksLikeCodeBlock = (text) => {
  if (!text || text.split('\n').length < 2) return false;
  const signals =
    (text.match(/[;{}]/g) || []).length +
    (text.match(/^\s*(public|private|protected|import|class|interface|@|<\/|#include)/gm) || [])
      .length;
  return signals >= 2;
};

const guessLanguage = (text) => {
  if (/^\s*<[\w!?]/.test(text) || /<\/\w+>/.test(text)) return 'xml';
  if (/^\s*import java\./m.test(text) || /\bpublic class\b/.test(text)) return 'java';
  if (/^\s*def \w+/m.test(text)) return 'python';
  if (/^\s*(function\s|const\s|let\s|=>)/m.test(text)) return 'javascript';
  if (/^\s*SELECT\s/im.test(text)) return 'sql';
  return 'plaintext';
};

const renderInlineMarks = (text) => {
  const parts = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(
        <strong key={match.index} style={{ fontWeight: 600 }}>
          {token.slice(2, -2)}
        </strong>
      );
    } else {
      parts.push(
        <code key={match.index} className="extra-info-inline-code">
          {token.slice(1, -1)}
        </code>
      );
    }
    last = match.index + token.length;
  }

  if (last < text.length) {
    parts.push(text.slice(last));
  }

  return parts.length ? parts : text;
};

const formatProseLines = (raw) => {
  if (!raw?.trim()) {
    return null;
  }

  const lines = raw.split('\n');

  return lines.map((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) {
      return <div key={idx} className="extra-info-spacer" />;
    }

    const letterItem = trimmed.match(/^([a-z]\))\s*(.+)$/i);
    const bulletItem = trimmed.match(/^[-•*]\s+(.+)$/);

    let content = trimmed;
    if (letterItem) content = letterItem[2];
    if (bulletItem) content = bulletItem[1];

    const colonAt = content.indexOf(': ');
    const hasTitle =
      colonAt > 0 &&
      colonAt < 90 &&
      !content.trimStart().startsWith('http');

    if (hasTitle) {
      const title = content.slice(0, colonAt + 1);
      const body = content.slice(colonAt + 2);
      return (
        <Block key={idx} className="extra-info-item">
          <ParagraphSmall className="extra-info-item-title">
            {letterItem && (
              <span className="extra-info-marker">{letterItem[1]} </span>
            )}
            {renderInlineMarks(title)}
          </ParagraphSmall>
          {body && (
            <ParagraphSmall className="extra-info-item-text">
              {renderInlineMarks(body)}
            </ParagraphSmall>
          )}
        </Block>
      );
    }

    if (letterItem || bulletItem) {
      return (
        <ParagraphSmall key={idx} className="extra-info-item extra-info-bullet">
          {letterItem && (
            <span className="extra-info-marker">{letterItem[1]} </span>
          )}
          {bulletItem && <span className="extra-info-marker">• </span>}
          {renderInlineMarks(content)}
        </ParagraphSmall>
      );
    }

    if (/^#{1,3}\s+/.test(trimmed)) {
      return (
        <LabelXSmall key={idx} className="extra-info-section-heading">
          {renderInlineMarks(trimmed.replace(/^#{1,3}\s+/, ''))}
        </LabelXSmall>
      );
    }

    return (
      <ParagraphSmall key={idx} className="extra-info-paragraph">
        {renderInlineMarks(trimmed)}
      </ParagraphSmall>
    );
  });
};

const renderProseWithImages = (raw) => {
  if (!raw?.trim()) {
    return null;
  }

  if (!/!\[[^\]]*\]\([^)]+\)/.test(raw)) {
    return <div className="extra-info-prose">{formatProseLines(raw)}</div>;
  }

  MD_IMAGE_REGEX.lastIndex = 0;
  const elements = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = MD_IMAGE_REGEX.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      const text = raw.slice(lastIndex, match.index);
      elements.push(
        <div key={`prose-${key++}`} className="extra-info-prose">
          {formatProseLines(text)}
        </div>
      );
    }
    elements.push(
      <div key={`img-${key++}`} className="extra-info-image-block">
        <ExtraInfoImage src={match[2]} alt={match[1] || 'Brainstorm scan'} />
      </div>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < raw.length) {
    elements.push(
      <div key={`prose-${key++}`} className="extra-info-prose">
        {formatProseLines(raw.slice(lastIndex))}
      </div>
    );
  }

  return elements;
};

const formatExtraInfoBody = (raw, mediumUrl) => {
  const images = mergeExtraInfoImages(raw, mediumUrl);
  const hasText = Boolean(raw?.trim());
  const hasImages = images.length > 0;

  if (!hasText && !hasImages) {
    return (
      <ParagraphSmall className="extra-info-empty">
        No additional notes for this question.
      </ParagraphSmall>
    );
  }

  const { text: textWithoutImages } = extractMarkdownImages(raw || '');
  const contentSource = (textWithoutImages.trim() || raw || '').trim();

  let mainContent = null;

  if (contentSource && !contentSource.includes('```') && looksLikeCodeBlock(contentSource)) {
    mainContent = (
      <div className="extra-info-code-wrap">
        <CodeBlock
          code={contentSource.trim()}
          language={guessLanguage(contentSource)}
          showLineNumbers={contentSource.split('\n').length > 2}
          variant="light"
        />
      </div>
    );
  } else if (contentSource) {
    const segments = splitExtraInfoContent(contentSource);

    mainContent = segments.map((segment, idx) => {
      if (segment.type === 'code') {
        const lang =
          segment.language === 'text' ? guessLanguage(segment.body) : segment.language;
        return (
          <div key={idx} className="extra-info-code-wrap">
            <CodeBlock
              code={segment.body}
              language={lang}
              showLineNumbers={segment.body.split('\n').length > 2}
              variant="light"
            />
          </div>
        );
      }

      if (looksLikeCodeBlock(segment.body)) {
        return (
          <div key={idx} className="extra-info-code-wrap">
            <CodeBlock
              code={segment.body}
              language={guessLanguage(segment.body)}
              showLineNumbers={segment.body.split('\n').length > 2}
              variant="light"
            />
          </div>
        );
      }

      return (
        <div key={idx}>{renderProseWithImages(segment.body)}</div>
      );
    });
  }

  return (
    <>
      {mainContent}
        {images.map((img, idx) => (
        <div key={`extra-img-${idx}`} className="extra-info-image-block">
          <ExtraInfoImage src={img.url} alt={img.alt} />
        </div>
      ))}
    </>
  );
};

const FormattedrawCode = ({
  questionItem,
  setReadStatus,
  data,
  setStatusMessage,
}) => {
  const [questionItemData] = useState(questionItem);
  const [currentReadStatus] = useState(data[questionItem.id]);

  const handleReadStatus = (status) => {
    setReadStatus(questionItem.id, status);
  };

  return (
    <div className="extra-info-container">
      <div className="extra-info-header">
        <Rating
          questionItemData={questionItemData}
          handleReadStatus={handleReadStatus}
          setStatusMessage={setStatusMessage}
          currentReadStatus={currentReadStatus}
        />
      </div>
      <div className="extra-info-body">
        {formatExtraInfoBody(questionItem.extraInfo, questionItem.mediumUrl)}
      </div>
    </div>
  );
};

export default FormattedrawCode;
